// 214 addendum 3 §24 [RULING 62 (b) — Laurent, 04/10] — A COLUMN THE SHEET DOES NOT HAVE LEAVES THE STORED VALUE AS
// IT IS; AN EMPTY CELL IN A COLUMN THE SHEET HAS STILL CLEARS IT. The clients sheet's rule since LEGAL-LANE fix 3 and
// 164 §A.1 (« written when the sheet states it, left alone when the sheet is silent »), applied to the two matter-list
// importers (CaseView's, CollectiveCaseView's): each built one `data` object from EVERY column it knows, reading an
// absent column as `undefined`, and spread it over the stored row — so a partial sheet (Ref, Title and Status, for a
// mass status edit) emptied every party list, date, description and rate of the matters it named, and a sheet of Ref
// and Title alone set every status « active » (docs/214-deploy-note.md §7.24). A NEW row has no stored value to keep
// and takes the importer's own defaults for the columns it lacks, as before; the caller decides which row it has.

import type { Translate } from "./interestRefusal"; // 214 addendum 5 §29 — the refusal's words

/** A sheet's column for a row key: the header's index as `findHeaderCol` answers it — −1 when the sheet lacks it. */
export type SheetColumn<T> = readonly [key: keyof T, headerIndex: number];

/** The keys a sheet does not carry (header index below 0), named — the keys a stored row keeps as it has them. */
export function absentColumns<T extends object>(columns: readonly SheetColumn<T>[]): (keyof T)[] {
  return columns.filter(([, headerIndex]) => headerIndex < 0).map(([key]) => key);
}

/** `data` without the keys whose column the sheet does not have: what a STORED row takes. Every other key stays, an
 *  empty cell included — the sheet stated it, so it clears. Built key by key (never a delete), so the result is a
 *  plain partial of the row. */
export function withoutAbsentColumns<T extends object>(data: T, columns: readonly SheetColumn<T>[]): Partial<T> {
  const absent = new Set<keyof T>(absentColumns(columns));
  const out: Partial<T> = {};
  for (const key of Object.keys(data) as (keyof T)[]) {
    if (!absent.has(key)) out[key] = data[key];
  }
  return out;
}

// 214 addendum 5 §29 [RULED 65 — Laurent, 05/10] — WHERE A SHEET'S ROW LANDS. Both matter lists' sheets carry each
// matter's Id as their first column. An import matches a row by its Ref; a row without a Ref is matched by its Id; a
// row with neither is a new matter, its ref given by the app. A copied row with a new Ref is a new matter, whatever its
// Id. ONE answer per row, asked once and read by the window (§25), the merges (`prevCc`) and the write alike — until
// this each importer asked by Ref alone (the collective list twice, once for the merges and once for the write), so a
// ref-less matter's row was created again on every round trip: « CC-2026/003 » on 04/10 and again on 05/10
// (docs/214-deploy-note.md §7.27 (3), §7.28a).

/** Where a row lands: `at` is the stored row's index in the import's working list; `id` the Id it was found by. */
export type SheetRowMatch =
  | { by: "ref"; at: number }
  | { by: "id"; at: number; id: number }
  | { by: "new" }
  | { by: "refused"; id: string };

const fieldOf = (row: unknown, key: "id" | "ref"): unknown =>
  typeof row === "object" && row !== null ? (row as Record<string, unknown>)[key] : undefined;

/** The Id cell as text: a number or a string as written, nothing for an empty cell, « ? » for anything else (a cell
 *  that holds something and names no matter is refused, never read as empty). */
function idText(cell: unknown): string {
  if (cell === undefined || cell === null) return "";
  if (typeof cell === "number") return String(cell);
  if (typeof cell === "string") return cell.trim();
  return "?";
}

/** `rows` is the import's working list — the stored matters, then the rows this sheet created (their temporary ids are
 *  negative, never a typed Id); `ref` the row's trimmed Ref; `idCell` its Id cell, `undefined` when the run reads no Id
 *  (a sheet without the column; « Replace all », whose rows are all created anew). By its Ref, its Id not read; else
 *  by its Id — a positive whole number naming a matter of the list; else a new matter. A row without a Ref whose Id
 *  names no matter of the list is REFUSED [ADVISOR — the clients sheet's rule, 164 §A.5(a)]: never created, never
 *  silently skipped. */
export function sheetRowMatch(rows: readonly unknown[], ref: string, idCell: unknown): SheetRowMatch {
  if (ref !== "") {
    const at = rows.findIndex((r) => fieldOf(r, "ref") === ref);
    return at >= 0 ? { by: "ref", at } : { by: "new" };
  }
  const raw = idCell === undefined ? "" : idText(idCell);
  if (raw === "") return { by: "new" };
  const id = /^\d+$/.test(raw) ? Number(raw) : 0;
  const at = id > 0 ? rows.findIndex((r) => fieldOf(r, "id") === id) : -1;
  return at >= 0 ? { by: "id", at, id } : { by: "refused", id: raw };
}

/** A row the Id rule refused: its row in the sheet (the header is row 1) and its Id cell as written. */
export interface RefusedRow {
  rn: number;
  id: string;
}

/** The refused rows' reasons, for the import's own summary toast — the app draws one toast at a time (212 [METHOD]
 *  (a)), so a reason in a toast of its own would be replaced before it was drawn. Nothing when none was refused. The
 *  clients sheet's reason (« Id N unknown »), in words the dictionary already holds — the row's word
 *  (`cc.import.col_row`: Ligne · Row · Rij) and `common.unknown` (inconnu · unknown · onbekend) — beside the sheet's
 *  own header « Id », the same in every language: « Id 999 inconnu (Ligne 4) ». No new word, so no seeded row. */
export function refusedReasons(t: Translate, refused: readonly RefusedRow[]): string {
  if (refused.length === 0) return "";
  const rowWord = t("cc.import.col_row", { fallback: "Row" });
  const unknown = t("common.unknown", { fallback: "unknown" });
  const lines = refused.map((r) => `Id ${r.id} ${unknown} (${rowWord} ${String(r.rn)})`);
  return ` — ${lines.join(", ")}`;
}
